import { useState, useEffect } from "react";
import axios from "axios";
import { Check, X, Eye, Lock, Home, User, Mail, Phone, MapPin, DollarSign } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminPage = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [adminCode, setAdminCode] = useState("");
  const [loading, setLoading] = useState(false);
  const [pendingListings, setPendingListings] = useState({ owner_listings: [], agent_listings: [] });
  const [selectedListing, setSelectedListing] = useState(null);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/admin/login`, { code: adminCode });
      setIsAuthenticated(true);
      localStorage.setItem("adminCode", adminCode);
      toast.success("Accès autorisé");
      fetchPendingListings(adminCode);
    } catch (error) {
      toast.error("Code incorrect");
    } finally {
      setLoading(false);
    }
  };

  const fetchPendingListings = async (code) => {
    try {
      const response = await axios.get(`${API}/admin/pending-listings?code=${code}`);
      setPendingListings(response.data);
    } catch (error) {
      toast.error("Erreur lors du chargement");
    }
  };

  const handleApprove = async (listingId) => {
    try {
      const code = localStorage.getItem("adminCode");
      await axios.post(`${API}/admin/approve-listing/${listingId}?code=${code}`);
      toast.success("Propriété publiée avec succès!");
      fetchPendingListings(code);
      setSelectedListing(null);
    } catch (error) {
      toast.error("Erreur lors de l'approbation");
    }
  };

  const handleReject = async (listingId) => {
    if (!window.confirm("Êtes-vous sûr de vouloir rejeter cette demande?")) return;
    try {
      const code = localStorage.getItem("adminCode");
      await axios.delete(`${API}/admin/reject-listing/${listingId}?code=${code}`);
      toast.success("Demande rejetée");
      fetchPendingListings(code);
      setSelectedListing(null);
    } catch (error) {
      toast.error("Erreur lors du rejet");
    }
  };

  useEffect(() => {
    const savedCode = localStorage.getItem("adminCode");
    if (savedCode) {
      setAdminCode(savedCode);
      setIsAuthenticated(true);
      fetchPendingListings(savedCode);
    }
  }, []);

  const handleLogout = () => {
    localStorage.removeItem("adminCode");
    setIsAuthenticated(false);
    setAdminCode("");
    setPendingListings({ owner_listings: [], agent_listings: [] });
  };

  if (!isAuthenticated) {
    return (
      <div className="pt-36 pb-20 min-h-screen bg-bone">
        <div className="max-w-md mx-auto px-6">
          <div className="bg-white rounded-3xl p-8 shadow-soft">
            <div className="text-center mb-8">
              <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <Lock className="w-8 h-8 text-primary" />
              </div>
              <h1 className="font-heading text-2xl font-medium text-charcoal">Panneau Admin</h1>
              <p className="text-muted-foreground mt-2">Entrez le code d'accès</p>
            </div>
            
            <form onSubmit={handleLogin} className="space-y-4">
              <Input
                type="password"
                placeholder="Code d'accès"
                value={adminCode}
                onChange={(e) => setAdminCode(e.target.value)}
                className="rounded-xl h-12 text-center text-lg tracking-widest"
                data-testid="admin-code-input"
              />
              <Button 
                type="submit" 
                className="w-full rounded-full h-12"
                disabled={loading || !adminCode}
                data-testid="admin-login-btn"
              >
                {loading ? "Vérification..." : "Accéder"}
              </Button>
            </form>
          </div>
        </div>
      </div>
    );
  }

  const totalPending = pendingListings.owner_listings.length + pendingListings.agent_listings.length;

  return (
    <div className="pt-36 pb-20 min-h-screen bg-bone">
      <div className="max-w-6xl mx-auto px-6">
        {/* Header */}
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="font-heading text-3xl font-medium text-charcoal">Panneau Admin</h1>
            <p className="text-muted-foreground">Gérez les demandes de publication</p>
          </div>
          <div className="flex items-center gap-4">
            <Badge variant="outline" className="text-lg px-4 py-2">
              {totalPending} en attente
            </Badge>
            <Button variant="outline" onClick={handleLogout} className="rounded-full">
              Déconnexion
            </Button>
          </div>
        </div>

        <div className="grid lg:grid-cols-2 gap-8">
          {/* Pending Owner Listings */}
          <div className="bg-white rounded-3xl p-6 shadow-soft">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                <Home className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <h2 className="font-heading text-xl font-medium">Propriétaires</h2>
                <p className="text-sm text-muted-foreground">{pendingListings.owner_listings.length} demande(s)</p>
              </div>
            </div>

            {pendingListings.owner_listings.length === 0 ? (
              <div className="text-center py-12 text-muted-foreground">
                <Home className="w-12 h-12 mx-auto mb-3 opacity-30" />
                <p>Aucune demande en attente</p>
              </div>
            ) : (
              <div className="space-y-4">
                {pendingListings.owner_listings.map((listing) => (
                  <div 
                    key={listing.id} 
                    className={`border rounded-2xl p-4 transition-all cursor-pointer hover:border-primary ${selectedListing?.id === listing.id ? 'border-primary bg-primary/5' : ''}`}
                    onClick={() => setSelectedListing(listing)}
                    data-testid={`listing-${listing.id}`}
                  >
                    <div className="flex justify-between items-start">
                      <div className="flex-1">
                        <h3 className="font-medium text-charcoal truncate">{listing.title || "Sans titre"}</h3>
                        <div className="flex items-center gap-2 mt-1 text-sm text-muted-foreground">
                          <MapPin className="w-3 h-3" />
                          <span>{listing.city}, {listing.province}</span>
                        </div>
                        <div className="flex items-center gap-2 mt-1">
                          <DollarSign className="w-3 h-3 text-green-600" />
                          <span className="text-green-600 font-medium">
                            {listing.price?.toLocaleString()} $
                          </span>
                        </div>
                      </div>
                      <Badge variant="secondary" className="bg-yellow-100 text-yellow-800">
                        En attente
                      </Badge>
                    </div>
                    
                    <div className="flex items-center gap-2 mt-3 pt-3 border-t text-sm text-muted-foreground">
                      <User className="w-3 h-3" />
                      <span>{listing.owner_name}</span>
                      <span className="mx-2">•</span>
                      <Mail className="w-3 h-3" />
                      <span className="truncate">{listing.owner_email}</span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Pending Agent Listings */}
          <div className="bg-white rounded-3xl p-6 shadow-soft">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 bg-purple-100 rounded-full flex items-center justify-center">
                <User className="w-5 h-5 text-purple-600" />
              </div>
              <div>
                <h2 className="font-heading text-xl font-medium">Agents</h2>
                <p className="text-sm text-muted-foreground">{pendingListings.agent_listings.length} demande(s)</p>
              </div>
            </div>

            {pendingListings.agent_listings.length === 0 ? (
              <div className="text-center py-12 text-muted-foreground">
                <User className="w-12 h-12 mx-auto mb-3 opacity-30" />
                <p>Aucune demande en attente</p>
              </div>
            ) : (
              <div className="space-y-4">
                {pendingListings.agent_listings.map((agent) => (
                  <div 
                    key={agent.id} 
                    className={`border rounded-2xl p-4 transition-all cursor-pointer hover:border-primary ${selectedListing?.id === agent.id ? 'border-primary bg-primary/5' : ''}`}
                    onClick={() => setSelectedListing(agent)}
                    data-testid={`agent-${agent.id}`}
                  >
                    <div className="flex justify-between items-start">
                      <div>
                        <h3 className="font-medium text-charcoal">{agent.name}</h3>
                        <div className="flex items-center gap-2 mt-1 text-sm text-muted-foreground">
                          <Mail className="w-3 h-3" />
                          <span>{agent.email}</span>
                        </div>
                        <div className="flex items-center gap-2 mt-1 text-sm text-muted-foreground">
                          <Phone className="w-3 h-3" />
                          <span>{agent.phone}</span>
                        </div>
                      </div>
                      <Badge variant="secondary" className="bg-yellow-100 text-yellow-800">
                        En attente
                      </Badge>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Selected Listing Detail Modal */}
        {selectedListing && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => setSelectedListing(null)}>
            <div className="bg-white rounded-3xl p-6 max-w-2xl w-full max-h-[80vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
              <div className="flex justify-between items-start mb-6">
                <h2 className="font-heading text-2xl font-medium">Détails de la demande</h2>
                <Button variant="ghost" size="icon" onClick={() => setSelectedListing(null)}>
                  <X className="w-5 h-5" />
                </Button>
              </div>

              <div className="space-y-4">
                {selectedListing.title && (
                  <div>
                    <label className="text-sm text-muted-foreground">Titre</label>
                    <p className="font-medium text-lg">{selectedListing.title}</p>
                  </div>
                )}

                {selectedListing.description && (
                  <div>
                    <label className="text-sm text-muted-foreground">Description</label>
                    <p className="text-charcoal">{selectedListing.description}</p>
                  </div>
                )}

                <div className="grid grid-cols-2 gap-4">
                  {selectedListing.price && (
                    <div>
                      <label className="text-sm text-muted-foreground">Prix</label>
                      <p className="font-medium text-green-600">{selectedListing.price?.toLocaleString()} $</p>
                    </div>
                  )}
                  
                  {selectedListing.city && (
                    <div>
                      <label className="text-sm text-muted-foreground">Ville</label>
                      <p className="font-medium">{selectedListing.city}, {selectedListing.province}</p>
                    </div>
                  )}

                  {selectedListing.bedrooms && (
                    <div>
                      <label className="text-sm text-muted-foreground">Chambres</label>
                      <p className="font-medium">{selectedListing.bedrooms}</p>
                    </div>
                  )}

                  {selectedListing.bathrooms && (
                    <div>
                      <label className="text-sm text-muted-foreground">Salles de bain</label>
                      <p className="font-medium">{selectedListing.bathrooms}</p>
                    </div>
                  )}
                </div>

                <div className="border-t pt-4 mt-4">
                  <label className="text-sm text-muted-foreground">Contact</label>
                  <div className="flex flex-wrap gap-4 mt-2">
                    <div className="flex items-center gap-2">
                      <User className="w-4 h-4 text-muted-foreground" />
                      <span>{selectedListing.owner_name || selectedListing.name}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Mail className="w-4 h-4 text-muted-foreground" />
                      <span>{selectedListing.owner_email || selectedListing.email}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Phone className="w-4 h-4 text-muted-foreground" />
                      <span>{selectedListing.owner_phone || selectedListing.phone}</span>
                    </div>
                  </div>
                </div>

                {selectedListing.image_url && (
                  <div>
                    <label className="text-sm text-muted-foreground">Image</label>
                    <img 
                      src={selectedListing.image_url} 
                      alt="Property" 
                      className="w-full h-48 object-cover rounded-xl mt-2"
                    />
                  </div>
                )}
              </div>

              <div className="flex gap-4 mt-8">
                <Button 
                  className="flex-1 rounded-full h-12 bg-green-600 hover:bg-green-700"
                  onClick={() => handleApprove(selectedListing.id)}
                  data-testid="approve-btn"
                >
                  <Check className="w-5 h-5 mr-2" />
                  Approuver et Publier
                </Button>
                <Button 
                  variant="destructive"
                  className="flex-1 rounded-full h-12"
                  onClick={() => handleReject(selectedListing.id)}
                  data-testid="reject-btn"
                >
                  <X className="w-5 h-5 mr-2" />
                  Rejeter
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminPage;
